import { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext(null)

const API_BASE = import.meta.env.VITE_API_BASE || '/api'

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('apiKey') || '')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Check if user is authenticated on mount
  useEffect(() => {
    checkAuth()
  }, [])

  async function checkAuth() {
    // First check for Cloudflare Access headers (if deployed with Access)
    try {
      const res = await fetch(`${API_BASE}/auth/check`, {
        headers: apiKey ? { 'X-API-Key': apiKey } : {}
      })
      
      if (res.ok) {
        const data = await res.json()
        if (data.authenticated) {
          setUser(data.user || { email: 'authenticated' })
        }
      }
    } catch (e) {
      // If auth check fails, we'll require manual login
      console.log('Auth check failed, manual login required')
    } finally {
      setLoading(false)
    }
  }

  async function login(key) {
    setLoading(true)
    setError(null)
    
    try {
      const res = await fetch(`${API_BASE}/videos?per_page=1`, {
        headers: { 'X-API-Key': key }
      })
      
      if (!res.ok) {
        throw new Error('Invalid API key')
      }
      
      setApiKey(key)
      sessionStorage.setItem('apiKey', key)
      setUser({ email: 'api-key-user' })
      return true
    } catch (e) {
      setError(e.message)
      return false
    } finally {
      setLoading(false)
    }
  }

  function logout() {
    setApiKey('')
    setUser(null)
    sessionStorage.removeItem('apiKey')
  }

  // Helper function for authenticated API calls
  async function api(path, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...(apiKey && { 'X-API-Key': apiKey }),
      ...options.headers
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers
    })

    if (res.status === 401) {
      logout()
      throw new Error('Session expired - please login again')
    }

    if (!res.ok) {
      const text = await res.text()
      // Attach structured fields so callers can inspect status code + parsed
      // JSON body without re-parsing the message. The message string is
      // preserved for back-compat with callers that only use err.message.
      let body = null
      try { body = text ? JSON.parse(text) : null } catch { /* leave null */ }
      const err = new Error(`[${res.status}] ${text}`)
      err.status = res.status
      err.body = body
      throw err
    }

    const text = await res.text()
    if (!text || text.trim() === '') {
      return { success: true }
    }

    try {
      return JSON.parse(text)
    } catch {
      return { success: true, data: text }
    }
  }

  // Like api() but does NOT set Content-Type (for multipart FormData uploads)
  async function apiRaw(path, options = {}) {
    const headers = {
      ...(apiKey && { 'X-API-Key': apiKey }),
      ...options.headers
    }

    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers
    })

    if (res.status === 401) {
      logout()
      throw new Error('Session expired - please login again')
    }

    if (!res.ok) {
      const text = await res.text()
      throw new Error(`[${res.status}] ${text}`)
    }

    const text = await res.text()
    if (!text || text.trim() === '') return { success: true }
    try { return JSON.parse(text) } catch { return { success: true, data: text } }
  }

  const value = {
    user,
    apiKey,
    loading,
    error,
    login,
    logout,
    api,
    apiRaw,
    isAuthenticated: !!user || !!apiKey
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
